// Pure helpers for the Colibo widget and its OAuth popup. Kept free of service imports so Jest can load them.

export const COLIBO_CALLBACK_PATH = '/colibo/auth/callback';

/** The sign-in popup hands the OAuth result back to the widget that opened it (see ColiboAuthCallback). */
export const COLIBO_CALLBACK_MESSAGE = 'iv-colibo-callback';

export interface IColiboCallback {
    code: string;
    state: string;
}

export type ColiboWidgetMode = 'loading' | 'loaded' | 'empty' | 'error' | 'unreachable' | 'token-invalid' | 'disconnected' | 'hidden';

/** Dispatched on window when the org turned Colibo off while the dashboard was open; the dashboard then drops the widget. */
export const COLIBO_NOT_ENABLED_EVENT = 'iv-colibo-not-enabled';

/** The redirect URI registered on the Colibo OAuth client; the identity-provider only accepts this path. */
export const buildColiboRedirectUri = (origin: string): string => `${origin.replace(/\/+$/, '')}${COLIBO_CALLBACK_PATH}`;

/** Reads the popup's hand-off; only trusted from our own origin and only with a non-empty code and state. */
export const parseColiboCallbackMessage = (event: { origin: string; data: unknown }, expectedOrigin: string): IColiboCallback | null => {
    if (event.origin !== expectedOrigin || typeof event.data !== 'object' || event.data === null) return null;
    const { type, code, state } = event.data as { type?: unknown; code?: unknown; state?: unknown };
    if (type !== COLIBO_CALLBACK_MESSAGE || typeof code !== 'string' || typeof state !== 'string' || !code || !state) return null;
    return { code, state };
};

/** The identity-provider answers Colibo failures with 422/502 and a stable `code` in the body. */
export const getColiboErrorCode = (error: unknown): string | undefined => {
    if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;
    return (error as { response?: { data?: { code?: string } } }).response?.data?.code;
};

export const modeForColiboErrorCode = (code: string | undefined): ColiboWidgetMode => {
    switch (code) {
        case 'colibo_token_invalid':
        case 'colibo_forbidden':
            return 'token-invalid';
        case 'colibo_not_connected':
            return 'disconnected';
        case 'colibo_unreachable':
            return 'unreachable';
        case 'colibo_not_enabled':
            return 'hidden'; // the org has Colibo off: the widget disappears instead of showing an error
        default:
            return 'error';
    }
};
